import type { FilterDefinition } from '@/components/filters/list-filter-types'

export type UnifiedSuggestion<TFilters, TCtx> =
  | { kind: 'category'; def: FilterDefinition<TFilters, TCtx> }
  | {
      kind: 'value'
      def: FilterDefinition<TFilters, TCtx>
      option: { value: string; label: string; count?: number }
    }
  | { kind: 'semantic'; query: string }

export function unifiedSuggestionRowId(listboxId: string, index: number): string {
  return `${listboxId}-opt-${index}`
}

function matchLabel(label: string, query: string): number {
  const normalized = label.toLowerCase()
  const q = query.trim().toLowerCase()
  if (!q) return 0
  if (normalized.startsWith(q)) return 2
  if (normalized.includes(q)) return 1
  return -1
}

export type BuildUnifiedSuggestionsOptions = {
  orderedKeys?: string[]
  maxValuesPerCategory?: number
  maxValues?: number
  includeSemantic?: boolean
}

export function buildUnifiedSuggestions<TFilters, TCtx>(
  query: string,
  definitions: FilterDefinition<TFilters, TCtx>[],
  filters: TFilters,
  ctx: TCtx,
  options: BuildUnifiedSuggestionsOptions = {},
): UnifiedSuggestion<TFilters, TCtx>[] {
  const trimmed = query.trim()
  const defsByKey = new Map(definitions.map((def) => [def.key, def]))

  // Layout catalog (`orderedKeys`): every dimension stays visible — `hidden` does not apply.
  const orderedDefs = options.orderedKeys?.length
    ? options.orderedKeys
        .map((key) => defsByKey.get(key))
        .filter((def): def is FilterDefinition<TFilters, TCtx> => Boolean(def))
    : definitions.filter((def) => !def.hidden?.(ctx))

  if (!trimmed) {
    return orderedDefs.map((def) => ({ kind: 'category', def }))
  }

  const result: UnifiedSuggestion<TFilters, TCtx>[] = []
  let valueCount = 0
  const maxValues = options.maxValues ?? 12
  const maxValuesPerCategory = options.maxValuesPerCategory ?? 6

  for (const def of orderedDefs) {
    const categoryScore = matchLabel(def.label, trimmed)
    if (!def.unified) {
      if (categoryScore >= 0) {
        result.push({ kind: 'category', def })
      }
      continue
    }

    if (def.unified.typeaheadValues === false) {
      if (categoryScore >= 0) {
        result.push({ kind: 'category', def })
      }
      continue
    }

    const selected = new Set(def.unified.getSelectedValues(filters, ctx))
    const matches = def.unified
      .getOptions(ctx)
      .map((option) => ({ option, score: matchLabel(option.label, trimmed) }))
      .filter(({ score, option }) => score >= 0 && !selected.has(option.value))
      .sort((a, b) => b.score - a.score || a.option.label.localeCompare(b.option.label))
      .slice(0, maxValuesPerCategory)

    if (matches.length > 0) {
      for (const { option } of matches) {
        if (valueCount >= maxValues) break
        result.push({ kind: 'value', def, option })
        valueCount += 1
      }
    } else if (categoryScore >= 0) {
      result.push({ kind: 'category', def })
    }
  }

  if (options.includeSemantic && trimmed) {
    result.push({ kind: 'semantic', query: trimmed })
  }

  return result
}
